/**
 * Canonical unit-equivalence classes for governed metric semantics.
 *
 * ONE authority (graph Phase 4 namespace law): every consumer comparing a
 * declared unit against a governed registry definition must compare CLASSES
 * from this function, never raw strings and never a coarser partition.
 *
 * - Raw strings over-block: the target-metric registry publishes COUNT for
 *   the same governed headcount quantity the compiled registry spells fte.
 * - A binary USD / non-USD partition under-blocks: USD is not USD_per_head
 *   (a level vs an intensity), and percent is not fte — collapsing them lets
 *   a semantically incompatible binding activate and produce incorrect drift
 *   comparisons.
 *
 * Everything outside the named equivalences compares exactly
 * (case-insensitively): usd, usd_per_head, and any future unit each form
 * their own class until an equivalence is GOVERNED here by name.
 */
export function canonicalGovernedUnitClass(unit: string): string {
  const normalized = unit.trim().toLowerCase();
  // count and fte are the SAME governed headcount quantity class — the
  // target-metric registry publishes COUNT for fte-denominated metrics.
  if (normalized === 'count' || normalized === 'fte') return 'headcount';
  if (normalized === 'percent' || normalized === '%' || normalized === 'pct') {
    return 'percent';
  }
  return normalized;
}

/** True when two declared units belong to the same governed quantity class. */
export function governedUnitsAgree(left: string, right: string): boolean {
  return canonicalGovernedUnitClass(left) === canonicalGovernedUnitClass(right);
}
